// Sequence Color Generation Utility

/**
 * Generates a color for a sequence identifier
 * @param sequenceId The sequence identifier ("MST", "01"-"99", "A"-"Z")
 * @returns Hex color string
 */
export function generateSequenceColor(sequenceId: string): string {
  // MST (Master) gets a distinct gold color
  if (sequenceId === 'MST') {
    return '#FFD700'; // Gold
  }
  
  // A-Z get soft pastel colors
  if (/^[A-Z]$/.test(sequenceId)) {
    return generateAlphaColor(sequenceId);
  }
  
  // Numeric sequences (01-99)
  const sequenceNumber = parseInt(sequenceId, 10);
  if (isNaN(sequenceNumber) || sequenceNumber < 1 || sequenceNumber > 99) {
    return '#3B82F6'; // Default blue
  }
  
  return generateNumericColor(sequenceNumber);
}

/**
 * Generates a pastel color for alphabetic sequences (A-Z)
 */
function generateAlphaColor(letter: string): string {
  const letterIndex = letter.charCodeAt(0) - 65; // A=0, B=1, ... Z=25
  
  // Spread hues across the color wheel
  const hue = (letterIndex * 14) % 360; // 360/26 ≈ 14
  
  // Use pastel colors (higher lightness, lower saturation)
  const saturation = 45;
  const brightness = 55;
  
  return hslToHex(hue, saturation, brightness);
}

/**
 * Generates a color for numeric sequences (1-99)
 */
function generateNumericColor(sequenceNumber: number): string {
  // Base hue values spread across the color wheel for maximum differentiation
  const baseHues = [
    15,   // Orange-Red
    45,   // Orange-Yellow
    75,   // Yellow-Green
    120,  // Green
    180,  // Cyan
    210,  // Light Blue
    240,  // Blue
    270,  // Purple
    300,  // Magenta
    330   // Pink-Red
  ];
  
  // For sequences 1-20, use the original logic
  if (sequenceNumber <= 20) {
    const hueIndex = (sequenceNumber - 1) % baseHues.length;
    const hueOffset = Math.floor((sequenceNumber - 1) / baseHues.length) * 36;
    const hue = (baseHues[hueIndex] + hueOffset) % 360;
    
    const minBrightness = 20;
    const maxBrightness = 40;
    const brightness = minBrightness + ((sequenceNumber - 1) % 10) * ((maxBrightness - minBrightness) / 9);
    
    const saturation = 75;
    return hslToHex(hue, saturation, brightness);
  }
  
  // For sequences 21-99, create variations based on the first 20
  const baseSequence = ((sequenceNumber - 1) % 20) + 1;
  const cycle = Math.floor((sequenceNumber - 1) / 20);
  
  const hueIndex = (baseSequence - 1) % baseHues.length;
  const hueOffset = Math.floor((baseSequence - 1) / baseHues.length) * 36;
  let hue = (baseHues[hueIndex] + hueOffset) % 360;
  
  hue = (hue + cycle * 15) % 360;
  
  let saturation, brightness;
  
  if (cycle === 1) {
    saturation = 60;
    brightness = 25 + ((baseSequence - 1) % 10) * 1.5;
  } else if (cycle === 2) {
    saturation = 85;
    brightness = 18 + ((baseSequence - 1) % 10) * 1.2;
  } else if (cycle === 3) {
    saturation = 70;
    brightness = 28 + ((baseSequence - 1) % 10) * 1.8;
  } else {
    saturation = 55;
    brightness = 22 + ((baseSequence - 1) % 10) * 2;
  }
  
  return hslToHex(hue, saturation, brightness);
}

/**
 * Converts HSL values to hex color string
 * @param h Hue (0-360)
 * @param s Saturation (0-100)
 * @param l Lightness (0-100)
 * @returns Hex color string
 */
function hslToHex(h: number, s: number, l: number): string {
  h = h % 360;
  s = Math.max(0, Math.min(100, s)) / 100;
  l = Math.max(0, Math.min(100, l)) / 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0, g = 0, b = 0;

  if (0 <= h && h < 60) {
    r = c; g = x; b = 0;
  } else if (60 <= h && h < 120) {
    r = x; g = c; b = 0;
  } else if (120 <= h && h < 180) {
    r = 0; g = c; b = x;
  } else if (180 <= h && h < 240) {
    r = 0; g = x; b = c;
  } else if (240 <= h && h < 300) {
    r = x; g = 0; b = c;
  } else if (300 <= h && h < 360) {
    r = c; g = 0; b = x;
  }

  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Calculates the brightness of a hex color
 * @param hex Hex color string
 * @returns Brightness percentage (0-100)
 */
export function calculateBrightness(hex: string): number {
  // Remove # if present
  const color = hex.replace('#', '');
  
  // Parse RGB values
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  
  // Calculate relative luminance (brightness)
  const brightness = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return Math.round(brightness * 100);
}

/**
 * Generates default sequence configurations for sequences 01-99
 * @returns Array of sequence configurations with string identifiers
 */
export function generateDefaultSequenceConfigs() {
  const configs = [];
  for (let i = 1; i <= 99; i++) {
    const sequenceId = String(i).padStart(2, '0');
    configs.push({
      sequenceNumber: sequenceId,
      color: generateSequenceColor(sequenceId),
      isActive: true
    });
  }
  return configs;
}

/**
 * Determines if white text should be used on the given background color
 * @param hex Background color hex string
 * @returns True if white text should be used, false if black text should be used
 */
export function shouldUseWhiteText(hex: string): boolean {
  const brightness = calculateBrightness(hex);
  return brightness < 50; // Use white text on dark backgrounds (< 50% brightness)
}